// Musiktheorie für Compose: Akkorde lesen, Voicings, Skalen, Melodie-Erkennung (YIN).
// Keine KI, reine Regeln. Läuft im Browser und im Server-Worker.

const PC: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11, H: 11 };

export const pitchClass = (s: string): number => {
  const base = PC[s[0]!];
  if (base === undefined) throw new Error(`Unbekannter Ton: ${s}`);
  const acc = s[1] === '#' ? 1 : s[1] === 'b' ? -1 : 0;
  return (base + acc + 12) % 12;
};

export interface ParsedChord { root: number; intervals: number[]; bass: number }

/** Liest Symbole wie F#m9, Dmaj7, A/C#, C#7sus4, G#m7b5 */
export function parseChord(symbol: string): ParsedChord {
  const [main, bass] = symbol.split('/');
  const m = main!.match(/^([A-H][#b]?)(.*)$/);
  if (!m) throw new Error(`Akkord nicht lesbar: ${symbol}`);
  const root = pitchClass(m[1]!);
  const q = m[2] ?? '';
  let iv = /^m(?!aj)/.test(q) ? [0, 3, 7] : [0, 4, 7];
  if (q.includes('m7b5')) iv = [0, 3, 6, 10];
  else {
    if (q.includes('maj7')) iv.push(11);
    else if (/7/.test(q)) iv.push(10);
    if (q.includes('9')) iv.push(14);
    if (q.includes('6')) iv.push(9);
  }
  if (q.includes('sus4')) iv[1] = 5;
  return { root, intervals: iv, bass: bass ? pitchClass(bass) : root };
}

/** Akkordtöne ab einer Untergrenze, aufsteigend */
export function voicing(symbol: string, low: number): number[] {
  const c = parseChord(symbol);
  return c.intervals
    .map((i) => {
      let n = c.root + i;
      while (n < low) n += 12;
      while (n >= low + 12 + (i > 11 ? 12 : 0)) n -= 12;
      return n;
    })
    .sort((a, b) => a - b);
}

export const SCALES = {
  minor: [0, 2, 3, 5, 7, 8, 10],
  major: [0, 2, 4, 5, 7, 9, 11],
} as const;

export function parseKey(key: string): { tonic: number; mode: 'minor' | 'major' } {
  const minor = /moll|m$|minor/i.test(key);
  const name = key.replace(/-?(Moll|Dur|minor|major)$/i, '').replace(/m$/, '');
  return { tonic: pitchClass(name), mode: minor ? 'minor' : 'major' };
}

export function inKey(midi: number, key: string): boolean {
  const { tonic, mode } = parseKey(key);
  return (SCALES[mode] as readonly number[]).includes((((midi - tonic) % 12) + 12) % 12);
}

export function snapToKey(midi: number, key: string): number {
  const r = Math.round(midi);
  if (inKey(r, key)) return r;
  const up = r + 1, down = r - 1;
  if (inKey(up, key) && Math.abs(midi - up) < Math.abs(midi - down)) return up;
  return inKey(down, key) ? down : up;
}

export interface DetectedNote { start: number; dur: number; midi: number }

/**
 * Einstimmige Tonhöhenerkennung (YIN) mit Notensegmentierung.
 * Eingabe: Mono-Samples. Funktioniert für Summen, Singen, Pfeifen (75 Hz bis 3,2 kHz).
 */
export function detectNotes(samples: Float32Array, sampleRate: number, maxSeconds = 24): DetectedNote[] {
  const ds = sampleRate > 32000 ? 2 : 1;
  const sr = sampleRate / ds;
  const n = Math.floor(Math.min(samples.length, sampleRate * maxSeconds) / ds);
  const x = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    let s = 0;
    for (let j = 0; j < ds; j++) s += samples[i * ds + j]!;
    x[i] = s / ds;
  }
  const W = 1024, H = 256, tmin = Math.floor(sr / 3200), tmax = Math.floor(sr / 75);
  const dfn = new Float32Array(tmax + 1), cm = new Float32Array(tmax + 1);
  let peak = 0;
  for (const v of x) peak = Math.max(peak, Math.abs(v));
  const gate = peak * 0.06;
  const frames: (number | null)[] = [];
  for (let st = 0; st + W + tmax < n; st += H) {
    let rms = 0;
    for (let i = 0; i < W; i++) rms += x[st + i]! * x[st + i]!;
    if (Math.sqrt(rms / W) < gate) { frames.push(null); continue; }
    for (let tau = 1; tau <= tmax; tau++) {
      let s = 0;
      for (let i = 0; i < W; i++) { const d = x[st + i]! - x[st + i + tau]!; s += d * d; }
      dfn[tau] = s;
    }
    let run = 0; cm[0] = 1;
    for (let tau = 1; tau <= tmax; tau++) { run += dfn[tau]!; cm[tau] = (dfn[tau]! * tau) / (run || 1); }
    let best = -1;
    for (let tau = tmin; tau < tmax; tau++) {
      if (cm[tau]! < 0.15) { while (tau + 1 < tmax && cm[tau + 1]! < cm[tau]!) tau++; best = tau; break; }
    }
    if (best > 0) {
      const a = cm[best - 1]!, b = cm[best]!, c = cm[best + 1]!, den = a - 2 * b + c;
      const bt = den ? best + (0.5 * (a - c)) / den : best;
      frames.push(69 + 12 * Math.log2(sr / bt / 440));
    } else frames.push(null);
  }
  const med = frames.map((v, i) => {
    const w = frames.slice(Math.max(0, i - 2), i + 3).filter((a): a is number => a != null).sort((p, q) => p - q);
    return v == null || !w.length ? null : w[Math.floor(w.length / 2)]!;
  });
  const hop = H / sr;
  type Seg = { start: number; end: number; vals: number[] };
  const segs: Seg[] = [];
  let cur: Seg | null = null;
  med.forEach((m, i) => {
    const t = i * hop;
    if (m == null) { if (cur) { segs.push(cur); cur = null; } return; }
    const mean = cur ? cur.vals.reduce((p, q) => p + q, 0) / cur.vals.length : 0;
    if (cur && Math.abs(m - mean) < 0.7) { cur.vals.push(m); cur.end = t + hop; }
    else { if (cur) segs.push(cur); cur = { start: t, end: t + hop, vals: [m] }; }
  });
  if (cur) segs.push(cur);
  const merged: Seg[] = [];
  for (const s of segs) {
    const l = merged[merged.length - 1];
    const avg = (v: number[]) => v.reduce((p, q) => p + q, 0) / v.length;
    if (l && s.start - l.end < 0.05 && Math.abs(avg(s.vals) - avg(l.vals)) < 0.5) { l.end = s.end; l.vals.push(...s.vals); }
    else merged.push(s);
  }
  return merged
    .filter((s) => s.end - s.start > 0.07)
    .map((s) => {
      const v = s.vals.slice(Math.min(2, s.vals.length - 1)).sort((p, q) => p - q);
      return { start: s.start, dur: s.end - s.start, midi: v[Math.floor(v.length / 2)]! };
    });
}

/** Erkannte Noten ins Raster und in die Tonart des Songs bringen */
export function quantizeNotes(notes: DetectedNote[], bpm: number, key: string, opts: { grid?: number; maxBeats?: number; low?: number; high?: number } = {}) {
  const grid = opts.grid ?? 0.5, maxBeats = opts.maxBeats ?? 16, low = opts.low ?? 62, high = opts.high ?? 74;
  if (!notes.length) return { notes: [] as { t: number; d: number; m: number }[], octaveShift: 0 };
  const spb = 60 / bpm, t0 = notes[0]!.start;
  let q = notes.map((n) => ({ t: Math.round((n.start - t0) / spb / grid) * grid, d: Math.max(grid, Math.round(n.dur / spb / grid) * grid), m: n.midi }))
    .filter((n) => n.t < maxBeats);
  q = q.filter((n, i, a) => i === 0 || n.t > a[i - 1]!.t);
  q.forEach((n, i) => {
    const nx = q[i + 1];
    if (nx && n.t + n.d > nx.t) n.d = Math.max(grid, nx.t - n.t);
    if (n.t + n.d > maxBeats) n.d = maxBeats - n.t;
  });
  const avg = q.reduce((a, n) => a + n.m, 0) / q.length;
  let shift = 0;
  while (avg + shift > high) shift -= 12;
  while (avg + shift < low) shift += 12;
  return { notes: q.map((n) => ({ t: n.t, d: n.d, m: snapToKey(n.m + shift, key) })), octaveShift: shift / 12 };
}
